"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateItineraryTransferRateMatrix = void 0;
const dayjs = require("dayjs");
const isBetween = require("dayjs/plugin/isBetween");
dayjs.extend(isBetween);
const _ = require("lodash");
const models_1 = require("../../models");
const transfers_1 = require("../transfers");
const generateItineraryTransferRateMatrix = async (rates, data, hotel_areas, course_areas, areas, margin_discount_percentage) => {
    return new Promise((resolve) => {
        // add an artifical delay to simulate the api call
        const dates = [];
        const duration_nights = data.trip.duration_days_nights || 0;
        // using the start date and the duration, generate the dates
        const duration = data?.trip?.type === models_1.TripType.GOLF_HOTEL
            ? duration_nights + 1
            : duration_nights;
        for (let i = 0; i < duration; i++) {
            dates.push(dayjs(data.trip.start_date).add(i, "day").format("YYYY-MM-DD"));
        }
        const rate_matrix = [];
        for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            // for each date get the rates between all courses and all hotels
            // loop through each hotel area
            hotel_areas.map((hotel) => {
                course_areas.map((course) => {
                    // if the from and to are the same, skip
                    if (hotel.id === course.id)
                        return;
                    const rate = getBestTransferRate(rates, hotel, course, date, data.trip.number_of_golfers || 0, "RETURN", margin_discount_percentage);
                    if (!rate)
                        return;
                    rate_matrix.push({
                        date: date,
                        from: hotel.id,
                        to: course.id,
                        rate: rate,
                    });
                });
            });
            // loop between each area and hotels (for airport / area transfers)  these are ONE WAY
            areas.map((area) => {
                hotel_areas.map((hotel) => {
                    // if the from and to are the same, skip
                    if (area.id === hotel.id)
                        return;
                    const rate = getBestTransferRate(rates, area, hotel, date, (data.trip.number_of_golfers || 0) +
                        (data.trip.number_of_non_golfers || 0), "ONE_WAY", margin_discount_percentage);
                    if (!rate)
                        return;
                    rate_matrix.push({
                        date: date,
                        from: area.id,
                        to: hotel.id,
                        rate: rate,
                    });
                });
            });
            // loop between each area and course
            areas.map((area) => {
                course_areas.map((course) => {
                    // if the from and to are the same, skip
                    if (area.id === course.id)
                        return;
                    const rate = getBestTransferRate(rates, area, course, date, data.trip.number_of_golfers || 0, "RETURN", margin_discount_percentage);
                    if (!rate)
                        return;
                    rate_matrix.push({
                        date: date,
                        from: area.id,
                        to: course.id,
                        rate: rate,
                    });
                });
            });
        }
        resolve(rate_matrix);
    });
};
exports.generateItineraryTransferRateMatrix = generateItineraryTransferRateMatrix;
const getBestTransferRate = (rates, outbound_from, outbound_to, date, passengers, type, margin_discount_percentage = 0) => {
    const transfer_rates = tryFindTransferRates(rates, outbound_from, outbound_to, date);
    const sorted = (0, transfers_1.sortAndGroupTransferRates)(transfer_rates, type, passengers, false);
    // get the cheapest rate by rate_total_price
    const transfer_rate = _.minBy(sorted.rates, "rate_total_price");
    if (transfer_rate) {
        let rate_margin = transfer_rate.rate_margin;
        let historic_price_increase = 1;
        if (transfer_rates?.historical) {
            if (transfer_rates.offset === 1) {
                historic_price_increase = 1.05;
            }
            if (transfer_rates.offset === 2) {
                historic_price_increase = 1.1;
            }
        }
        // apply the margin percentage discount
        if (margin_discount_percentage) {
            const percentage = (100 - margin_discount_percentage) / 100;
            rate_margin = Number(rate_margin * percentage);
        }
        const rate_config = {
            historic_rate: transfer_rates?.historical
                ? {
                    ...transfer_rate,
                    percentage_increase: 5 * (transfer_rates?.offset || 1),
                    date: dayjs(date)
                        .subtract(transfer_rates?.offset, "year")
                        .format("YYYY-MM-DD"),
                }
                : null,
            max_pax: transfer_rate.rate_max_pax,
            rate: {
                ...transfer_rate?.rates,
                type: transfer_rates?.historical
                    ? "CUSTOM"
                    : transfer_rate?.rates?.type,
                estimated: transfer_rates?.historical ? true : false,
            },
            custom: transfer_rates?.historical ? true : false,
            vehicle: transfer_rate?.vehicle,
            outbound_cost: Number((transfer_rate.rate_outbound_cost_price * historic_price_increase).toFixed(2)),
            outbound_margin: Number((transfer_rate.rate_outbound_margin * historic_price_increase).toFixed(2)),
            outbound_price: Number((transfer_rate.rate_outbound_sell_price * historic_price_increase).toFixed(2)),
            outbound_total: Number((transfer_rate.rate_outbound_total_price * historic_price_increase).toFixed(2)),
            inbound_cost: Number((transfer_rate.rate_inbound_cost_price * historic_price_increase).toFixed(2)),
            inbound_margin: Number((transfer_rate.rate_inbound_margin * historic_price_increase).toFixed(2)),
            inbound_price: Number((transfer_rate.rate_inbound_sell_price * historic_price_increase).toFixed(2)),
            inbound_total: Number((transfer_rate.rate_inbound_total_price * historic_price_increase).toFixed(2)),
            cost: Number((transfer_rate.rate_cost_price * historic_price_increase).toFixed(2)),
            margin: Number((rate_margin * historic_price_increase).toFixed(2)),
            price: Number((transfer_rate.rate_cost_price +
                rate_margin * historic_price_increase).toFixed(2)),
            total: Number(((transfer_rate.rate_cost_price +
                rate_margin * historic_price_increase) *
                transfer_rate.rate_number_of_vehicles).toFixed(2)),
            quantity: transfer_rate.rate_number_of_vehicles,
            vat: transfer_rate.supplier?.offerings?.transfers?.offerings?.transfers
                ?.vat,
            withholding_tax: transfer_rate.supplier?.offerings?.transfers?.offerings?.transfers
                ?.withholding_tax,
        };
        return rate_config;
    }
    return null;
};
const tryFindTransferRates = (rates, outbound_from, outbound_to, date) => {
    const current_rates = loadTransferRates(rates, outbound_from, outbound_to, date);
    if (current_rates?.outbound?.length > 0 &&
        current_rates?.inbound?.length > 0) {
        return {
            ...current_rates,
            offset: 0,
            historical: false,
        };
    }
    else {
        // try to get one year previously
        let historical_rates = loadTransferRates(rates, outbound_from, outbound_to, dayjs(date).subtract(1, "year").format("YYYY-MM-DD"));
        if (historical_rates?.outbound?.length > 0 &&
            historical_rates?.inbound?.length > 0) {
            return {
                ...historical_rates,
                offset: 1,
                historical: true,
            };
        }
        // try to get two years previously
        historical_rates = loadTransferRates(rates, outbound_from, outbound_to, dayjs(date).subtract(2, "year").format("YYYY-MM-DD"));
        if (historical_rates?.outbound?.length > 0 &&
            historical_rates?.inbound?.length > 0) {
            return {
                ...historical_rates,
                offset: 2,
                historical: true,
            };
        }
    }
};
const loadTransferRates = (rates, outbound_from, outbound_to, date) => {
    const results = rates.filter((rate) => {
        return (((rate.from_area === outbound_from.id &&
            rate.to_area === outbound_to.id) ||
            (rate.from_area === outbound_to.id &&
                rate.to_area === outbound_from.id)) &&
            // date between the rates start and end date inclusive
            dayjs(date).isBetween(rate.start_date, rate.end_date, null, "[]"));
    });
    return {
        outbound: results,
        inbound: results,
    };
};
